import React, { useState, useCallback } from "react";
import { Modal, Form, Input, notification } from "antd";
import { css } from "glamor";

import {
    getFileFormat,
    validateBulkRequestFile,
    transformBulkUploadFile,
    xlsxType,
} from "@arachnys/flow-app/src/util";
import FileUpload from "@arachnys/styleguide/FileUpload";
import Button from "@arachnys/styleguide/Button";
import { getAuthorization } from "@arachnys/flow-app/src/api";

const styles = {
    actionButtons: css({
        display: "flex",
        justifyContent: "flex-end",
    }),
    cancelButton: css({
        marginRight: 10,
    }),
};

const BulkRequestForm = ({
    form: { validateFields, getFieldDecorator },
    handleSubmit,
    isSubmitting,
    onCancel,
}) => {
    const [fileList, setFile] = useState([]);

    const validateFile = file => {
        if (validateBulkRequestFile(getFileFormat(file))) {
            setFile([file]);
        }
        return false;
    };

    const onRemove = () => {
        setFile([]);
    };

    const onSubmit = useCallback(
        e => {
            e.preventDefault();
            validateFields((err, values) => {
                if (!err) {
                    handleSubmit(values);
                }
            });
        },
        [validateFields, handleSubmit]
    );

    return (
        <Form onSubmit={onSubmit}>
            <Form.Item>
                <label htmlFor="name">Request Name</label>
                {getFieldDecorator("name", {
                    rules: [
                        {
                            required: true,
                            message: "Please provide request name!",
                        },
                    ],
                })(<Input placeholder="name" />)}
            </Form.Item>
            <Form.Item>
                {getFieldDecorator("file", {
                    rules: [
                        {
                            required: true,
                            message: "Please upload *.csv file!",
                        },
                    ],
                })(
                    <FileUpload
                        hint="Valid file format：.csv, .xlsx"
                        accept={`.csv,.xlsx,text/csv,${xlsxType}`}
                        beforeUpload={validateFile}
                        onRemove={onRemove}
                        fileList={fileList}
                        multiple={false}
                    />
                )}
            </Form.Item>
            <Form.Item>
                <label htmlFor="notes">Notes</label>
                {getFieldDecorator("notes", {
                    initialValue: "",
                })(<Input.TextArea />)}
            </Form.Item>
            <div className={styles.actionButtons}>
                <Form.Item>
                    <Button.Primary
                        className={`${styles.cancelButton}`}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button.Primary>
                </Form.Item>
                <Form.Item>
                    <Button.Primary disabled={isSubmitting} htmlType="submit">
                        Submit Request
                    </Button.Primary>
                </Form.Item>
            </div>
        </Form>
    );
};

const WrappedBulkRequestForm = Form.create()(BulkRequestForm);

const BulkRequestPopup = ({ visible, onClose, onSuccess }) => {
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async values => {
        setSubmitting(true);

        const formData = new FormData();
        const data = {
            ...values,
            file: transformBulkUploadFile(values.file.file),
        };
        const auth = await getAuthorization();

        for (const key in data) {
            formData.append(key, data[key]);
        }

        const request = new Request("/api/tickets/bulk_upload_request/", {
            method: "POST",
            headers: {
                Authorization: auth,
            },
            body: formData,
        });
        const response = await fetch(request);

        if (response.ok) {
            notification.success({
                message: "Batch request upload success",
                description: "File uploaded successfully",
            });
            onSuccess();
        } else {
            notification.error({
                message: "Batch request upload error",
                description:
                    "File upload failed! Please, check your connection or permissions!",
            });
        }

        setSubmitting(false);
        onClose();
    };

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            title="Batch request"
            footer={null}
        >
            <WrappedBulkRequestForm
                handleSubmit={handleSubmit}
                isSubmitting={submitting}
                onCancel={onClose}
            />
        </Modal>
    );
};

export default BulkRequestPopup;
